// StaffSalary.jsx — dynamic non-portal designation tabs
import { mergeSortedTabs } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, ChevronRightIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { ALL_DESIGNATIONS, NON_PORTAL_COLORS } from '../constants/staffDesignations';
import { useSchoolModules } from '../hooks/useSchoolModules';

const BASE_PORTAL_TABS = [
  { key:'teacher',     label:'Teachers',     color:'#3b6ef8' },
  { key:'coordinator', label:'Coordinators', color:'#8b5cf6' },
  { key:'finance',     label:'Finance',      color:'#10c98e' },
];
const initials = n => (n||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmtMoney = n => n ? `Rs. ${Number(n).toLocaleString('en-PK')}` : '—';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ss * { box-sizing:border-box; } .ss { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .ss-title { font-size:1.4rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:3px; }
  .ss-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }
  .role-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .role-tab  { padding:7px 16px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .role-tab.active { color:white; border-color:transparent; }
  .ss-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .ss-search-wrap { position:relative; flex:1; max-width:320px; }
  .ss-search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; pointer-events:none; }
  .ss-search { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 32px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .ss-search:focus { border-color:#3b6ef8; }
  .staff-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:1rem; }
  .staff-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.3rem; cursor:pointer; transition:transform .15s,box-shadow .15s,border-color .15s; animation:ssFade .35s ease both; }
  .staff-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.08); border-color:#ddd6fe; }
  .staff-card.own-card { opacity:.55; cursor:not-allowed; }
  .staff-card.own-card:hover { transform:none; box-shadow:none; border-color:#e8ebf4; }
  @keyframes ssFade { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .sc-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:.9rem; }
  .sc-avatar { width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:.84rem; font-weight:700; color:white; }
  .sc-arrow { width:26px; height:26px; border-radius:7px; background:#f0f2f7; display:flex; align-items:center; justify-content:center; }
  .sc-arrow svg { width:13px; height:13px; stroke-width:2.5; color:#9094a8; }
  .sc-name  { font-size:.88rem; font-weight:700; color:#0f1123; margin-bottom:2px; }
  .sc-sub   { font-size:.7rem; color:#9094a8; }
  .sc-salary { font-size:.8rem; font-weight:700; color:#7c3aed; font-family:'JetBrains Mono',monospace; margin-top:8px; }
  .ss-empty { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:3rem 2rem; text-align:center; color:#9094a8; font-size:.82rem; }
  .ss-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .ss-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
`;

export default function StaffSalary() {
  const navigate = useNavigate();
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const PORTAL_TABS = [
    ...BASE_PORTAL_TABS,
    ...(libraryEnabled ? [{ key: 'librarian',     label: 'Librarians',   color: '#f59e0b' }] : []),
    ...(hostelEnabled  ? [{ key: 'hostel_member', label: 'Hostel Staff', color: '#ec4899' }] : []),
  ];
  const PORTAL_ROLES = PORTAL_TABS.map(t => t.key);
  const [activeRole,      setActiveRole]      = useState('');
  const [staff,           setStaff]           = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [search,          setSearch]          = useState('');
  const [myUserId,        setMyUserId]        = useState(null);

  const isAll      = activeRole === '';
  const isPortal   = PORTAL_ROLES.includes(activeRole);
  const portalTab  = PORTAL_TABS.find(t => t.key === activeRole);
  const tabColor   = isAll ? '#6b7194' : (portalTab?.color || NON_PORTAL_COLORS[ALL_DESIGNATIONS.indexOf(activeRole) % NON_PORTAL_COLORS.length] || '#f59e0b');

  useEffect(() => {
    api.get('/admin/me').then(r => setMyUserId(String(r.data?._id||''))).catch(()=>{});
  }, []);

  useEffect(() => {
    setLoading(true); setSearch('');
    api.get(`/admin/salary/staff?role=${activeRole}`)
      .then(r => setStaff(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [activeRole]);

  const filtered = staff.filter(s => !search ||
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.employeeId?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ss"><style>{css}</style>
      <div className="ss-title">Staff Salary</div>
      <div className="ss-sub">Manage monthly salaries for all staff roles.</div>

      <div className="role-tabs">
        <button className={`role-tab ${isAll?'active':''}`}
          style={isAll ? {background:'#6b7194'} : {}}
          onClick={() => setActiveRole('')}>All</button>
        {mergeSortedTabs(PORTAL_TABS, ALL_DESIGNATIONS, NON_PORTAL_COLORS).map(t => (
          <button key={t.key} className={`role-tab ${activeRole===t.key?'active':''}`}
            style={activeRole===t.key ? {background:t.color} : {}}
            onClick={() => setActiveRole(t.key)}>{t.label}</button>
        ))}
      </div>

      <div className="ss-toolbar">
        <div className="ss-search-wrap">
          <MagnifyingGlassIcon />
          <input className="ss-search" placeholder={`Search ${isAll ? 'all staff' : (portalTab?.label || activeRole)}…`}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? <div className="ss-loading"><div className="ss-spin"/>Loading…</div>
      : filtered.length===0 ? <div className="ss-empty">No {isAll ? 'staff' : (portalTab?.label || activeRole)} found.</div>
      : (
        <div className="staff-grid">
          {filtered.map((s, i) => {
            const isOwn = myUserId && String(s._id) === String(myUserId);
            return (
              <div key={s._id}
                className={`staff-card${isOwn?' own-card':''}`}
                style={{animationDelay:`${i*.04}s`}}
                onClick={() => !isOwn && navigate(`/staff-salary/${s._id}?role=${s.role||activeRole}&isPortal=${s.isPortal!==undefined?s.isPortal:isPortal}`)}>
                <div className="sc-top">
                  <div className="sc-avatar" style={{background:`linear-gradient(135deg,${tabColor},${tabColor}99)`}}>{initials(s.name)}</div>
                  {isOwn
                    ? <span style={{fontSize:'.68rem',color:'#3b6ef8',fontWeight:600,padding:'3px 8px',borderRadius:99,background:'#eef2ff'}}>👤 You — use My Salary</span>
                    : <div className="sc-arrow"><ChevronRightIcon /></div>}
                </div>
                <div className="sc-name">{s.name}</div>
                <div className="sc-sub">{s.email||s.employeeId||s.role||activeRole}</div>
                {isAll && <div style={{fontSize:'.66rem',fontWeight:600,textTransform:'capitalize',color:'#6b7194',marginTop:2}}>{s.role}</div>}
                <div className="sc-salary">
                  <BanknotesIcon style={{width:13,height:13,strokeWidth:2,display:'inline',marginRight:4}}/>
                  {fmtMoney(s.monthlySalary)}/mo
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}